import { useState, useRef } from 'react';
import { adminService } from '../services/adminService';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook to log search queries with debouncing.
 * @param {string} searchContext - The context of the search (e.g., 'FLEET', 'CONTRACTS', 'ARCHIVE').
 * @param {number} delay - Debounce delay in ms (default 2000ms).
 */
export function useSearchLogger(searchContext, delay = 2000) {
    const { currentUser } = useAuth();
    const [lastLoggedQuery, setLastLoggedQuery] = useState('');
    const timeoutRef = useRef(null);

    // Sanitize query to prevent XSS
    const sanitizeQuery = (query) => {
        if (!query || typeof query !== 'string') return '';
        // Remove any HTML tags and dangerous characters
        return query
            .replace(/<[^>]*>/g, '') // Remove HTML tags
            .replace(/[<>"']/g, '') // Remove dangerous characters
            .trim()
            .substring(0, 200); // Limit length
    };

    const logSearch = (query) => {
        if (!query || query.trim().length < 2) return; // Ignore short/empty queries
        
        const sanitizedQuery = sanitizeQuery(query);
        if (!sanitizedQuery) return; // Ignore if sanitization removed everything
        if (sanitizedQuery === lastLoggedQuery) return; // Ignore duplicate consecutive logs

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            // Perform the log with sanitized query
            adminService.logUserAction(
                `SEARCH_${searchContext}`,
                `Searched for "${sanitizedQuery}"`,
                currentUser,
                { query: sanitizedQuery }
            );
            setLastLoggedQuery(sanitizedQuery);
        }, delay);
    };

    return logSearch;
}
